import { useMemo, useState } from "react";
import moment from "moment";
import { Page } from "../../components/Page";
import { Search } from "../../components/Search";
import { useOnboardingList } from "./hooks/useOnboardingTracking";
import { ProvisioningUser } from "./types";
import { OverallStatusBadge } from "./components/StatusBadge";
import { UserDetailPanel } from "./components/UserDetailPanel";

type Tab = "active" | "completed" | "all";
type SetupModeFilter = "" | "self_serve" | "concierge" | "null";

const PMS_OPTIONS = [
  { value: "", label: "All PMSs" },
  { value: "MEWS", label: "Mews" },
  { value: "CLOUDBEDS", label: "CloudBeds" },
  { value: "RESNEXUS", label: "ResNexus" },
  { value: "WEBREZPRO", label: "WebRezPro" },
  { value: "LODGIX", label: "Lodgix" },
  { value: "LODGIFY", label: "Lodgify" },
  { value: "THINKRESERVATIONS", label: "ThinkReservations" },
  { value: "INNROAD", label: "InnRoad" },
  { value: "REZSTREAM", label: "RezStream" },
  { value: "INNQUEST", label: "InnQuest" },
  { value: "HOSTAWAY", label: "Hostaway" },
  { value: "IQWARE", label: "IQware" },
  { value: "LITTLEHOTELIER", label: "Little Hotelier" },
];

// "null" is the backend sentinel for rows with no setupMode (legacy users
// who onboarded before the self-serve/concierge fork).
const SETUP_MODE_OPTIONS: { value: SetupModeFilter; label: string }[] = [
  { value: "", label: "All modes" },
  { value: "self_serve", label: "Self-Serve" },
  { value: "concierge", label: "Concierge" },
  { value: "null", label: "Legacy" },
];

function SetupModeBadge({
  setupMode,
}: {
  setupMode: ProvisioningUser["setupMode"];
}): React.ReactElement {
  if (setupMode === "self_serve") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
        Self-Serve
      </span>
    );
  }
  if (setupMode === "concierge") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">
        Concierge
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 text-content-secondary">
      Legacy
    </span>
  );
}

// Active = anything not fully terminal. The backend's `status` filter only
// accepts a single status, so for "Active" we query all rows and filter
// client-side. For "Completed" we use the backend filter directly.
function isActiveUser(u: ProvisioningUser): boolean {
  return u.overallStatus !== "COMPLETED";
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  return moment(iso).fromNow();
}

function ProgressBar({
  user,
}: {
  user: ProvisioningUser;
}): React.ReactElement {
  const completed = user.steps.filter(
    (s) => s.status === "COMPLETED" || s.status === "SKIPPED",
  ).length;
  const total = user.steps.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const colorByStatus =
    user.overallStatus === "FAILED"
      ? "bg-red-500"
      : user.overallStatus === "AWAITING_MANUAL"
        ? "bg-orange-500"
        : user.overallStatus === "IN_PROGRESS"
          ? "bg-blue-500"
          : "bg-green-500";
  return (
    <div className="w-[200px]">
      <div className="h-1.5 bg-neutral-200 rounded overflow-hidden">
        <div
          className={`h-full ${colorByStatus} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-content-secondary mt-1">
        {completed}/{total} steps
      </div>
    </div>
  );
}

function CurrentStepLabel({
  user,
}: {
  user: ProvisioningUser;
}): React.ReactElement {
  // Find the first step that's not terminal — that's the "current" one the
  // user is waiting on. If all are completed/skipped, say "Done".
  const current =
    user.steps.find(
      (s) =>
        s.status === "IN_PROGRESS" ||
        s.status === "AWAITING_MANUAL" ||
        s.status === "FAILED" ||
        s.status === "PENDING",
    ) ?? null;

  if (!current) {
    return (
      <div className="text-xs text-content-secondary">All steps done</div>
    );
  }
  return (
    <div>
      <div className="text-xs text-content-secondary">
        Step {current.order} of {user.steps.length}
      </div>
      <div className="text-sm font-medium text-content-primary truncate max-w-[220px]">
        {current.label}
      </div>
    </div>
  );
}

export function OnboardingTracking() {
  const [tab, setTab] = useState<Tab>("active");
  const [search, setSearch] = useState("");
  const [pmsFilter, setPmsFilter] = useState("");
  const [setupModeFilter, setSetupModeFilter] = useState<SetupModeFilter>("");
  const [page, setPage] = useState(1);
  const limit = 25;

  // Store the selected userId only — the live user object is looked up from
  // the latest list data on every render. This way, when the list refetches
  // (e.g., after a mutation invalidates the cache), the open drawer
  // automatically re-renders with the fresh user state instead of a stale
  // snapshot taken at row-click time.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Backend filter mapping:
  //  - "completed" → status=COMPLETED
  //  - "active" → no status filter, we client-side filter out COMPLETED rows
  //  - "all" → no status filter
  const backendStatusFilter = tab === "completed" ? "COMPLETED" : undefined;

  const { data, isLoading, isFetching } = useOnboardingList({
    status: backendStatusFilter,
    pmsType: pmsFilter || undefined,
    search: search || undefined,
    setupMode: setupModeFilter || undefined,
    page,
    limit,
  });

  const rows: ProvisioningUser[] = useMemo(() => {
    const all = data?.data ?? [];
    if (tab === "active") return all.filter(isActiveUser);
    return all;
  }, [data, tab]);

  const selectedUser = useMemo(
    () =>
      selectedUserId
        ? (data?.data ?? []).find((u) => u.userId === selectedUserId) ?? null
        : null,
    [data, selectedUserId],
  );

  // Counts for tab pills
  const activeCount = useMemo(
    () => (data?.data ?? []).filter(isActiveUser).length,
    [data],
  );
  const completedCount = useMemo(
    () => (data?.data ?? []).filter((u) => !isActiveUser(u)).length,
    [data],
  );
  const totalCount = data?.total ?? 0;

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const tabClass = (isActive: boolean) =>
    `px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
      isActive
        ? "border-brand-primary text-content-primary"
        : "border-transparent text-content-secondary hover:text-content-primary"
    }`;

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1 1xl:w-[unset]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-black">
              Onboarding Tracking
            </h1>
            <div className="text-sm text-content-secondary mt-1">
              SEM provisioning pipeline
              {isFetching && !isLoading && " · refreshing…"}
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="bg-white rounded-t-md border border-border-primary border-b-0 px-4 flex">
          <button
            type="button"
            onClick={() => {
              setTab("active");
              setPage(1);
            }}
            className={tabClass(tab === "active")}
          >
            Active {tab !== "completed" && `(${activeCount})`}
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("completed");
              setPage(1);
            }}
            className={tabClass(tab === "completed")}
          >
            Completed{" "}
            {tab === "completed" && `(${totalCount})`}
            {tab !== "completed" && `(${completedCount})`}
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("all");
              setPage(1);
            }}
            className={tabClass(tab === "all")}
          >
            All ({totalCount})
          </button>
        </div>

        {/* Search + PMS filter bar */}
        <div className="bg-white border-x border-border-primary p-4 flex gap-3 items-center flex-wrap border-b border-border-primary">
          <div className="flex-1 min-w-[240px]">
            <Search
              value={search}
              onChange={(v) => {
                setSearch(v);
                setPage(1);
              }}
            />
          </div>
          <select
            className="h-10 px-3 rounded border border-neutral-400 text-sm bg-white"
            value={pmsFilter}
            onChange={(e) => {
              setPmsFilter(e.target.value);
              setPage(1);
            }}
          >
            {PMS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className="h-10 px-3 rounded border border-neutral-400 text-sm bg-white"
            value={setupModeFilter}
            onChange={(e) => {
              setSetupModeFilter(e.target.value as SetupModeFilter);
              setPage(1);
            }}
          >
            {SETUP_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-b-md border border-border-primary border-t-0 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-content-secondary text-sm">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-content-secondary text-sm">
              {tab === "active"
                ? "No active onboardings. Users appear here once SEM provisioning starts."
                : tab === "completed"
                  ? "No completed onboardings yet."
                  : "No onboarding data. Users appear here once SEM provisioning runs for them."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-border-primary">
                <tr className="text-left text-xs font-medium text-content-secondary uppercase">
                  <th className="px-4 py-3">Hotel</th>
                  <th className="px-4 py-3">PMS</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Current Step</th>
                  <th className="px-4 py-3">Progress</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr
                    key={user.userId}
                    onClick={() => setSelectedUserId(user.userId)}
                    className="border-b border-border-primary last:border-b-0 hover:bg-neutral-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-content-primary">
                        {user.hotelName ?? "(no hotel name)"}
                      </div>
                      <div className="text-xs text-content-secondary">
                        {user.userEmail ?? "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 text-content-secondary">
                        {user.pmsType ?? "?"}
                      </span>
                      {user.isGa4Only && (
                        <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                          GA4
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <SetupModeBadge setupMode={user.setupMode} />
                    </td>
                    <td className="px-4 py-3">
                      <OverallStatusBadge status={user.overallStatus} />
                    </td>
                    <td className="px-4 py-3">
                      <CurrentStepLabel user={user} />
                    </td>
                    <td className="px-4 py-3">
                      <ProgressBar user={user} />
                    </td>
                    <td className="px-4 py-3 text-xs text-content-secondary whitespace-nowrap">
                      {formatRelative(user.startedAt)}
                    </td>
                    <td className="px-4 py-3 text-xs text-content-secondary whitespace-nowrap">
                      {formatRelative(user.lastUpdatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="mt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-8 px-3 rounded border border-neutral-400 text-sm disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-sm text-content-secondary">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="h-8 px-3 rounded border border-neutral-400 text-sm disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserDetailPanel
          user={selectedUser}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </Page>
  );
}
